"use client"

import React, { useState, useCallback, useEffect, useRef } from "react"
import {
  TextField,
  Autocomplete,
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  InputAdornment,
  ClickAwayListener 
} from "@mui/material"
import { LocationOn, MyLocation, GpsFixed as GpsFixedIcon } from "@mui/icons-material"
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete"
import { makeStyles } from "@mui/styles"

const SEARCH_FORM_CONSTANTS = {
  REQUEST_LOCATION_ACCESS: "Please provide location access",
}

const locationCache = { currentLocation: undefined }

const getLocationNamefromLatandLng = async ({ latitude, longitude }) => {
  if (locationCache.currentLocation) return locationCache.currentLocation
  let geocoder,
    placeName = ""
  geocoder = await new window.google.maps.Geocoder()
  const latlng = { lat: Number.parseFloat(latitude), lng: Number.parseFloat(longitude) }
  await geocoder.geocode({ location: latlng }).then((response) => {
    if (response.results[0]) {
      response.results[0]?.address_components?.map((place, index, arr) => {
        if (!place?.types?.includes("plus_code")) {
          placeName += `${place.short_name}${index !== arr.length - 1 ? ", " : ""}`
        }
      })
      if (!placeName) placeName = response.results[0]?.formatted_address
    }
  })
  locationCache.currentLocation = placeName
  return placeName || "Near me"
}

const fetchLatLongFromPlaceID = async (placeId) => {
  try {
    const results = await getGeocode({ placeId });
    const { lat, lng } = getLatLng(results[0]);
    return { lat, lng };
  } catch (error) {
    console.error("Error fetching lat/lng: ", error);
    return null;
  }
};

const useStyles = makeStyles({
  option: {
    boxShadow: "0px 2px 10px rgba(0,0,0,0.08)",
    maxHeight: "12em",
  },
})

const LocationAutocomplete = ({
  value, 
  onChange, 
  error,
  onError, // Optional callback for error handling
  placeholder = "Select location",
  showUseCurrentButton = true,
  autoDetect = false,
  initialAddress,
  variant = "outlined",
  fullWidth = true,
  size = "medium",
  sx = {},
  ...props 
}) => {
  const styles = useStyles()
  const [manualOptions, setManualOptions] = useState([])
  const [currentLocation, setCurrentLocation] = useState(null)
  const [locationError, setLocationError] = useState("")
  const [locationAccessRequested, setLocationAccessRequested] = useState(false)
  const [locationPermissionStatus, setLocationPermissionStatus] = useState(null)
  const [getLocationLoading, setGetLocationLoading] = useState(false)
  const [openLocationDropDown, setOpenLocationDropDown] = useState(false)
  const [latAndLng, setLatAndLng] = useState(null)

  const {
    ready,
    value: inputValue,
    suggestions: { status, data: locationSuggestions },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "us" },
      types: ["geocode", "establishment"],
    },
    debounce: 500,
  })

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      const errorMsg = "Geolocation is not supported by this browser."
      setLocationError(errorMsg)
      if (onError) onError(errorMsg)
      return
    }

    setGetLocationLoading(true)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords
          const placeName = await getLocationNamefromLatandLng({ latitude, longitude }) || "Near Me"
          const latLngObj = { lat: latitude, lng: longitude }
          
          const location = {
            type: "nearMe",
            value: latLngObj,
            placeName: placeName,
            fromEdit: false,
            latAndLng: latLngObj,
            description: placeName,
            place_id: "current_location",
            coordinates: { lat: latitude, lng: longitude },
          }
          
          setCurrentLocation(location)
          setLatAndLng(latLngObj)
          setValue(placeName, false)
          setGetLocationLoading(false)
          setOpenLocationDropDown(false)
          setLocationError("")
          onChange(location)
        } catch (error) {
          console.error("Error getting location details:", error)
          const errorMsg = "Unable to get location details"
          setLocationError(errorMsg)
          setGetLocationLoading(false)
          if (onError) onError(errorMsg)
        }
      },
      (error) => {
        console.error("Geolocation error:", error)
        let errorMsg = "Unable to access your location. Please enter manually."
        if (error.code === error.PERMISSION_DENIED) {
          errorMsg = SEARCH_FORM_CONSTANTS.REQUEST_LOCATION_ACCESS
          setLocationPermissionStatus("denied")
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMsg = "Location unavailable. Please enter manually."
          setLocationPermissionStatus("unavailable")
        } else {
          setLocationPermissionStatus("error")
        }
        setLocationError(errorMsg)
        setGetLocationLoading(false)
        if (onError) onError(errorMsg)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }, [onChange, onError, setValue])

  const handleSelect = async (description) => {
    setValue(description, false)
    clearSuggestions()

    try {
      const results = await getGeocode({ address: description })
      const { lat, lng } = await getLatLng(results[0])
      const latLngObj = { lat, lng }

      const location = {
        type: "place",
        value: results[0].place_id,
        placeName: description,
        fromEdit: false,
        latAndLng: latLngObj,
        description,
        place_id: results[0].place_id,
        coordinates: { lat, lng },
      }

      setLatAndLng(latLngObj)
      onChange(location)
      setLocationError("")
    } catch (error) {
      console.error("Error getting coordinates:", error)
      const errorMsg = "Error getting coordinates for selected location"
      setLocationError(errorMsg)
      if (onError) onError(errorMsg)
    }
  }

  const NearMeOption = useCallback(() => {
    return {
      description: getLocationLoading ? "Fetching your location" : "Near Me",
      id: "nearMe",
    }
  }, [getLocationLoading])

  const renderLocationSuggestions = (props, suggestion, options) => {
    if (suggestion.id === "nearMe") {
      return (
        <li key={suggestion.id} {...props}>
          <Grid container alignItems="center" gap={1}>
            <Grid item>
              {getLocationLoading ? (
                <CircularProgress size={16} />
              ) : (
                <GpsFixedIcon htmlColor="#036FCB" sx={{ fontSize: 18 }} />
              )}
            </Grid>
            <Grid item xs>
              <Typography
                variant="body2"
                style={{
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                {suggestion.description}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )
    }
    
    const {
      place_id,
      structured_formatting: { main_text, secondary_text },
    } = suggestion

    return (
      <Box key={place_id}>
        <li {...props}>
          <Grid container alignItems="center">
            <Grid item>
              <LocationOn sx={{ mr: 1, color: "#666", fontSize: 18 }} />
            </Grid>
            <Grid item xs>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: "0.8rem",
                }}
              >
                {main_text}
              </span>
              <Typography variant="body2" sx={{ color: "#9ca3af", fontSize: "0.75rem", opacity: "1" }}>
                {secondary_text}
              </Typography>
            </Grid>
          </Grid>
        </li>
        {props["data-option-index"] === options.length - 1 && (
          <Box
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "0px 10px",
            }}
          >
            <img
              src="https://member-portal-resources.s3.amazonaws.com/google_logo/google_logo/desktop/google_on_white.png"
              alt="Powered by Google"
              style={{ height: 16 }}
            />
          </Box>
        )}
      </Box>
    )
  }

  const handleLocationInputChange = async (event, selectedOption) => {
    if (!selectedOption) return
    if (selectedOption.id === "nearMe") {
      getCurrentLocation()
      event.stopPropagation()
    } else {
      const latLng = await fetchLatLongFromPlaceID(selectedOption.place_id)
      const location = {
        type: "place",
        value: selectedOption.place_id,
        placeName: selectedOption.description,
        fromEdit: false,
        latAndLng: latLng,
        description: selectedOption.description,
        place_id: selectedOption.place_id,
        coordinates: latLng,
      }
      
      setLatAndLng(latLng)
      setValue(selectedOption.description, false)
      onChange(location)
      setLocationError("")
    }
  }

  const handleLocationSearchQuery = (event, value) => {
    if (value === "Near Me") {
      return setValue(value, false)
    }
    if (value === "") {
      setLatAndLng(null)
      clearSuggestions()
      setManualOptions([])
      if (value !== inputValue) {
        const emptyLocation = {
          type: "",
          value: "",
          placeName: "",
          latAndLng: null,
        }
        onChange(emptyLocation)
      }
      setLocationError("")
      return setValue("", false)
    }
    setValue(value, value.length > 3)
  }

  const handleGetOptionLabel = (option) => {
    if (typeof option === "string") return option
    if (option?.description === "Near Me") return ""
    return option?.description || value?.placeName || ""
  }

  // Auto-request location on component mount
  useEffect(() => {
    if (autoDetect && !locationAccessRequested && !initialAddress) {
      setLocationAccessRequested(true)
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            setLocationPermissionStatus("granted")
            const { latitude, longitude } = position.coords
            const placeName = await getLocationNamefromLatandLng({ latitude, longitude }) || "Near Me"
            const latLngObj = { lat: latitude, lng: longitude }
            
            const location = {
              type: "nearMe",
              value: latLngObj,
              placeName: placeName,
              fromEdit: false,
              latAndLng: latLngObj,
              description: placeName,
              place_id: "current_location",
              coordinates: { lat: latitude, lng: longitude },
            }
            
            setLatAndLng(latLngObj)
            setValue(placeName, false)
            onChange(location)
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED) setLocationPermissionStatus("denied")
            else if (error.code === error.POSITION_UNAVAILABLE) setLocationPermissionStatus("unavailable")
            else setLocationPermissionStatus("error")
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        )
      } else {
        setLocationPermissionStatus("unsupported")
      }
    }
  }, [locationAccessRequested, onChange, setValue, autoDetect, initialAddress])

  // Handle initial address from patient info
  useEffect(() => {
    if (initialAddress) {
      setValue(initialAddress, true)
      setOpenLocationDropDown(true)
    }
  }, [initialAddress, setValue])

  const options = [
    ...(showUseCurrentButton ? [NearMeOption()] : []),
    ...locationSuggestions,
    ...manualOptions,
  ]

  return (
    <Box>
      {ready && (
        <ClickAwayListener onClickAway={() => setOpenLocationDropDown(false)}>
          <Autocomplete
            value={value?.placeName || inputValue}
            onChange={handleLocationInputChange}
            inputValue={inputValue}
            onInputChange={handleLocationSearchQuery}
            options={options}
            getOptionLabel={handleGetOptionLabel}
            isOptionEqualToValue={(option, value) => option?.place_id === value?.place_id}
            open={openLocationDropDown}
            onOpen={() => setOpenLocationDropDown(true)}
            autoComplete
            popupIcon=""
            disablePortal={true}
            includeInputInList
            filterSelectedOptions
            filterOptions={(x) => x}
            freeSolo={true}
            size={size}
            ListboxProps={{
              style: { maxHeight: "15vh" },
              className: styles.option,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                variant={variant}
                fullWidth={fullWidth}
                error={error}
                size={size}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    fontSize: "0.8rem",
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#035fcb",
                        borderWidth: 1,
                      },
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9ca3af",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d1d5db",
                    },
                    ...(error && {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d32f2f",
                        borderWidth: 1,
                      },
                    }),
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.8rem",
                  },
                  ...sx,
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: "#9ca3af", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, suggestion) =>
              renderLocationSuggestions(props, suggestion, locationSuggestions)
            }
            noOptionsText="Start typing to search locations"
          />
        </ClickAwayListener>
      )}

      {showUseCurrentButton && (
        <Button
          startIcon={<MyLocation sx={{ fontSize: 16 }} />}
          onClick={getCurrentLocation}
          variant="text"
          size="small"
          disabled={getLocationLoading}
          sx={{ 
            mt: 1, 
            textTransform: "none",
            fontSize: "0.75rem",
            color: "#035fcb",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline"
            }
          }}
        >
          {getLocationLoading ? "Getting location..." : "Use current location"}
        </Button>
      )}

      {locationError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1, fontSize: "0.7rem" }}>
          {locationError}
        </Typography>
      )}
    </Box>
  )
}

export default LocationAutocomplete